import { useEffect, useState, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import { AUTH_API_URL } from '../config';
import { useOfflineSession } from './OfflineSessionContext';
import { listConversations, saveConversationSnapshot, reconcileServerConversations, subscribeToConversations } from './conversationRepository';

// Shared by the mobile menu and desktop sidebar. A failed request must never
// replace a readable local history with an empty array.
export default function useConversationHistory({ enabled = true, refreshTrigger, projectId, scope } = {}) {
  const { offlineReadOnly, revalidate } = useOfflineSession();
  const revalidateRef = useRef(revalidate);
  revalidateRef.current = revalidate;
  const native = Capacitor.isNativePlatform();
  const userId = localStorage.getItem('userId');
  const token = localStorage.getItem('token');
  const [conversations, setConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    let serverItems = [];
    let importing = false;
    let phase = 'local';
    const filter = (items) => {
      let links = {};
      try { links = JSON.parse(localStorage.getItem('projectChatLinks') || '{}'); } catch (_) { /* legacy optional map */ }
      return items.filter((item) => projectId
        ? (item.projectId || links[item.id]) === projectId
        : scope !== 'general' || !(item.projectId || links[item.id]));
    };
    const loadLocal = async () => {
      if (!native || !userId) return [];
      const local = await listConversations(userId);
      if (active) {
        const merged = new Map(serverItems.map((item) => [item.id, item]));
        local.forEach((item) => merged.set(item.id, item));
        setConversations(filter([...merged.values()]));
      }
      return local;
    };
    const unsubscribe = native ? subscribeToConversations((event) => { if (!importing && event.userId === userId) loadLocal().catch(() => {}); }) : () => {};
    setConversations([]);
    setError(null);
    setIsLoading(true);
    (async () => {
      try { await loadLocal(); } catch (storageError) { if (active) setError(storageError); }
      if (offlineReadOnly || (native && navigator.onLine === false) || !token) return;
      let url = `${AUTH_API_URL}/conversations`;
      if (projectId) url += `?projectId=${encodeURIComponent(projectId)}`;
      else if (scope === 'general') url += '?projectId=null';
      phase = 'network';
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!active) return;
      phase = 'local';
      if (!Array.isArray(response.data)) throw new Error('Invalid conversation history response');
      serverItems = response.data;
      setConversations(filter(serverItems));
      if (native && userId) {
        importing = true;
        await reconcileServerConversations(userId, serverItems.map((item) => item.id), { projectId, scope });
        // The existing API has no pagination. Limit automatic local import;
        // opening an older conversation saves it separately in Chat.
        const recent = [...serverItems].sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0)).slice(0, 100);
        for (const item of recent) {
          if (!active || localStorage.getItem('userId') !== userId) return;
          if (item.messages?.length) await saveConversationSnapshot(userId, item, { source: 'server' });
        }
        await loadLocal();
        importing = false;
      }
    })().catch((loadError) => {
      importing = false;
      if (active) {
        setError(loadError);
        if (native && phase === 'network' && (!loadError.response || [401, 403].includes(loadError.response.status))) revalidateRef.current?.();
      }
    })
      .finally(() => { if (active) setIsLoading(false); });
    return () => { active = false; unsubscribe(); };
  }, [enabled, native, userId, token, offlineReadOnly, refreshTrigger, projectId, scope]);

  return { conversations, isLoading, error };
}
